import { useQuery } from "@tanstack/react-query";
import { toast } from "../../../utils/toast";
import { showDocument } from "../../../utils/documentViewer";
import { errorMessageOf, pageParams, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY } from "../model";

// Shared by HEC's review screens (journey steps 8 and 10) and the evaluator's

export const APPLICATION_STATES = [
  { value: "SUBMITTED", label: "Submitted", color: "gray" },
  { value: "IN_REVIEW", label: "In review", color: "yellow" },
  { value: "REVIEWED", label: "Reviewed", color: "blue" },
  { value: "WITH_DG", label: "With the DG", color: "yellow" },
  { value: "WITH_SPONSOR", label: "With the partner", color: "yellow" },
  { value: "WITH_MINISTER", label: "With the Minister", color: "brand" },
  { value: "RETURNED_TO_HEC", label: "Returned to HEC", color: "yellow" },
  { value: "GRANTED", label: "Granted", color: "green" },
  { value: "APPROVED", label: "Approved", color: "green" },
  { value: "REJECTED", label: "Rejected", color: "red" },
  { value: "RESERVE", label: "Reserve", color: "brand" },
  { value: "NOT_AWARDED", label: "Not awarded", color: "red" },
  // Only on applications decided in the abroad records, before the DG and sponsor steps
  { value: "AWARDED", label: "Awarded", color: "green" },
  { value: "WITHDRAWN", label: "Withdrawn", color: "gray" },
  // A draft the student never submitted; staff never see one
  { value: "EXPIRED", label: "Expired", color: "gray" },
];

// The states a scholarship's applications can reach, in the order of its flow
const SPONSORED_STATES = ["SUBMITTED", "IN_REVIEW", "REVIEWED", "WITH_DG", "WITH_SPONSOR", "GRANTED", "REJECTED", "RESERVE", "NOT_AWARDED", "AWARDED", "WITHDRAWN"];
const GOVERNMENT_LOAN_STATES = ["SUBMITTED", "IN_REVIEW", "REVIEWED", "WITH_DG", "WITH_MINISTER", "RETURNED_TO_HEC", "APPROVED", "REJECTED", "RESERVE", "NOT_AWARDED", "AWARDED", "WITHDRAWN"];
// Private institution loans started after the abroad records, so none was awarded there
const LOAN_STATES = GOVERNMENT_LOAN_STATES.filter((s) => s !== "AWARDED");

/** The states to filter a scholarship's applicants by: only those its type's flow uses. */
export const applicationStatesFor = (type) => {
  const values = { COOPERATION: SPONSORED_STATES, GOVERNMENT: GOVERNMENT_LOAN_STATES }[type] ?? LOAN_STATES;
  return values.map((value) => APPLICATION_STATES.find((s) => s.value === value));
};

// Waiting for an evaluator's marks
export const BEING_MARKED = ["SUBMITTED", "IN_REVIEW"];
// Every evaluator has marked these: only a reviewed application gets an outcome or goes on to the DG
export const FULLY_MARKED = [
  "REVIEWED",
  "AWARDED",
  "RESERVE",
  "NOT_AWARDED",
  "WITH_DG",
  "WITH_SPONSOR",
  "WITH_MINISTER",
  "RETURNED_TO_HEC",
  "APPROVED",
  "GRANTED",
  "REJECTED",
  "RECEIVED_BY_PARTNER",
  "RECEIVED_BY_UNIVERSITY",
];
export const applicationStateOf = (value) => APPLICATION_STATES.find((s) => s.value === value) ?? { value, label: value ?? "—", color: "gray" };

export const OUTCOMES = [
  { value: "AWARDED", label: "Awarded", color: "green" },
  { value: "RESERVE", label: "Reserve", color: "brand" },
  { value: "NOT_AWARDED", label: "Not awarded", color: "red" },
];
export const outcomeOf = (value) => OUTCOMES.find((o) => o.value === value) ?? { value, label: value, color: "gray" };
// A sponsored scholarship's list nominates students, whom the sponsor then grants
const NOMINATION_LABELS = { AWARDED: "Nominated", NOT_AWARDED: "Not nominated" };
export const outcomesFor = (sponsored) => (sponsored ? OUTCOMES.map((o) => ({ ...o, label: NOMINATION_LABELS[o.value] ?? o.label })) : OUTCOMES);

export const EVALUATION_STATES = [
  { value: "NOT_STARTED", label: "Not started", color: "gray" },
  { value: "DRAFT", label: "In progress", color: "yellow" },
  { value: "SUBMITTED", label: "Marked", color: "green" },
  { value: "CONFLICTED", label: "Removed (conflict)", color: "red" },
];
export const evaluationStateOf = (value) => EVALUATION_STATES.find((s) => s.value === value) ?? { value, label: value, color: "gray" };

export const COURSE_SECTORS = [
  "AGRICULTURE",
  "ARTS",
  "AVIATION",
  "BUSINESS",
  "CONSTRUCTION",
  "EDUCATION",
  "ENVIRONMENTAL",
  "HEALTH",
  "ICT",
  "INFRASTRUCTURE",
  "LAW",
  "MINING",
  "NUCLEAR_SCIENCE_AND_TECHNOLOGY",
  "SCIENCE",
  "SPORT",
  "TOURISM",
].map((value) => ({ value, label: value === "ICT" ? "ICT" : value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, " ") }));
export const sectorLabel = (value) => COURSE_SECTORS.find((s) => s.value === value)?.label ?? "—";

export { fullName } from "../model";
export const formatScore = (score) => (score == null ? "—" : Number(score).toFixed(1));
export const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { timeZone: "Africa/Kigali", dateStyle: "medium", timeStyle: "short" }) : "—");
// A local date and time without a zone (a flight's departure), shown as written
export const formatLocalDateTime = (local) => (local ? new Date(`${local}Z`).toLocaleString("en-GB", { timeZone: "UTC", dateStyle: "medium", timeStyle: "short" }) : "—");
// Short names of the file types students may upload
export const TYPE_NAMES = { "application/pdf": "PDF", "image/jpeg": "JPEG", "image/png": "PNG" };

// "PDF · 1.2 MB": files are named by the system, so screens show what the file is, not its name
export const fileSummary = (file) => `${TYPE_NAMES[file.detectedType] ?? "File"} · ${formatSize(file.sizeBytes)}`;

export const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

export const REVIEW_KEY = [...SCHOLARSHIPS_KEY, "review"];
// A scholarship's award list, as `{ data, etag }`: shared by the award list panel and the DG's sign-off
export const awardListKey = (scholarshipId) => [...REVIEW_KEY, "award-list", scholarshipId];

// One page of a scholarship's submitted applications
export const useScholarshipApplications = (scholarshipId, filters) =>
  useQuery({
    queryKey: [...REVIEW_KEY, "applications", scholarshipId, filters],
    placeholderData: (previous) => previous,
    queryFn: async () => (await scholarshipsApi.get(`admin/scholarships/${scholarshipId}/applications`, { params: pageParams(filters) })).data,
  });

// Every active application of a scholarship, best mean score first
export const useRanking = (scholarshipId) =>
  useQuery({
    queryKey: [...REVIEW_KEY, "ranking", scholarshipId],
    queryFn: async () => (await scholarshipsApi.get(`admin/scholarships/${scholarshipId}/ranking`)).data,
  });

export const adminApplicationKey = (id) => [...REVIEW_KEY, "application", id];
// `basePath` is "admin" for HEC, "staff" for the partner (who reads only its candidates)
export const useAdminApplication = (id, basePath = "admin") =>
  useQuery({
    queryKey: [...adminApplicationKey(id), basePath],
    enabled: Boolean(id),
    queryFn: () => scholarshipsApi.get(`${basePath}/applications/${id}`),
  });

/**
 * Shows a document in the app's document viewer. The link is short-lived, so it is fetched on
 * click, then the file itself (only PDF, JPEG and PNG are accepted) is loaded from it.
 */
export const openDocument = async (path, fileName = "") => {
  try {
    await showDocument(async () => {
      const link = (await scholarshipsApi.get(path)).data.url;
      const response = await fetch(link);
      if (!response.ok) throw new Error(`The file link answered ${response.status}`);
      return response.blob();
    }, fileName);
  } catch (error) {
    console.error(`Failed to open document ${path}:`, error);
    // Only the API's own answers carry a reason; the file link's do not
    const fallback = "Could not open the document. Please try again.";
    toast.error(error.isAxiosError ? errorMessageOf(error, fallback) : fallback);
  }
};

// The award list's pre-selection, as the backend makes it: "the top fully marked applicant is", "the top 3 fully marked applicants are", or null without a quota (nobody is pre-selected)
export const topOf = (quota) => (quota == null ? null : quota === 1 ? "the top fully marked applicant is" : `the top ${quota} fully marked applicants are`);
